import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  ArrowRight,
  Gamepad2,
  Trophy,
  Brain,
  ShoppingBag,
} from "lucide-react";
import { CATEGORIES, PRODUCTS, CATEGORY_HERO_IMAGES } from "../data/products";
import ProductCard from "../components/ProductCard";
import { useStore } from "../context/StoreContext";
import Coin from "../components/Coin";
import DailyGift from "../components/DailyGift";
import FunFact from "../components/FunFact";
import SixtySevenBooth from "../components/SixtySevenBooth";
import AddToHomeFlyer from "../components/AddToHomeFlyer";
import AdBanner from "../components/AdBanner";
import { BRAIN_GAMES, ARCADE_GAMES } from "../data/games";
import usePageMeta from "../hooks/usePageMeta";

const featured = PRODUCTS.filter((p) =>
  ["p1", "p7", "p13", "p18", "p23", "p28", "p33", "p37"].includes(p.id)
);

// Pick 6 arcade games as "suggested today" — rotate by hour so repeat visits vary.
const pickSuggested = () => {
  const hour = new Date().getHours();
  const list = [...ARCADE_GAMES];
  const seeded = list.slice(hour % list.length).concat(list.slice(0, hour % list.length));
  return seeded.slice(0, 6);
};

const Home = () => {
  const { name, coins, avatar } = useStore();
  usePageMeta(
    "67Cafe — Play, Learn, Shop & Win",
    "67Cafe is a bright, playful hangout for everyone: play mini-games, solve math & trivia quizzes, earn coins and spend them on toys, candy, books, plushies and more."
  );

  const suggested = useMemo(pickSuggested, []);

  return (
    <div className="pb-8" data-testid="home-page">
      <AddToHomeFlyer />
      {/* Compact post-login greeting */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-3 sm:pt-4">
        <div
          className="chunky-card p-3 sm:p-4 flex items-center gap-3 overflow-hidden"
          style={{
            background:
              "radial-gradient(circle at 20% 20%, #FFE0F5 0%, #FFF9E6 60%)",
          }}
          data-testid="home-hero"
        >
          <Link
            to="/profile"
            className="rounded-full flex items-center justify-center shrink-0"
            style={{
              width: 48,
              height: 48,
              background: "#FF5A5F",
              border: "3px solid #2b2b2b",
              boxShadow: "0 3px 0 #2b2b2b",
            }}
            data-testid="home-hero-avatar"
            aria-label="Open my profile"
          >
            <span className="text-2xl bob">{avatar || "🎉"}</span>
          </Link>
          <div className="flex-1 min-w-0">
            <h1
              className="font-display text-base sm:text-xl leading-tight truncate"
              data-testid="home-hero-title"
            >
              Hey{" "}
              <span style={{ color: "#FF5A5F" }}>{name || "friend"}</span>! You
              have{" "}
              <span
                className="inline-flex items-center gap-1 whitespace-nowrap"
                style={{ color: "#FFB400" }}
              >
                <Coin size={16} />
                {coins}
              </span>{" "}
              coins.
            </h1>
            <p
              className="text-xs sm:text-sm mt-0.5 truncate"
              style={{ color: "#484848" }}
            >
              Play, learn &amp; shop with silly loot.
            </p>
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            <Link
              to="/games"
              className="btn-3d btn-sm"
              data-testid="hero-play-games"
              aria-label="Play games"
            >
              <Gamepad2 size={14} strokeWidth={3} />
              <span className="hidden sm:inline">Play</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Daily gift */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-6">
        <DailyGift />
      </section>

      {/* Sponsored — self-hosted home-hero ad slot (kid-safe, admin-managed).
          Renders nothing when no active ad is scheduled. */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-6">
        <AdBanner slot="home_hero" />
      </section>

      {/* 67 Booth — pay coins, hear the shout */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-6">
        <SixtySevenBooth />
      </section>

      {/* Sponsored — secondary Home slot below the booth. */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-6">
        <AdBanner slot="home_secondary" />
      </section>

      {/* Brain Boosters — math, quizzes, board games */}
      <section
        className="mx-auto max-w-6xl px-4 sm:px-6 mt-8"
        data-testid="home-brain-strip"
      >
        <div className="flex items-end justify-between mb-3 gap-3">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl flex items-center gap-2">
              <Brain size={26} strokeWidth={3} style={{ color: "#5F27CD" }} />
              Brain Boosters
            </h2>
            <p className="text-sm sm:text-base mt-1" style={{ color: "#484848" }}>
              Math quizzes, word problems, trivia &amp; classic board games.
            </p>
          </div>
          <Link
            to="/games"
            className="btn-3d btn-sm btn-purple"
            data-testid="home-see-all-brain"
          >
            See all
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {BRAIN_GAMES.map((g) => (
            <Link
              key={g.id}
              to={`/games/${g.id}`}
              className="chunky-card pushable p-4 text-center"
              data-testid={`home-brain-${g.id}`}
              style={{ background: g.color + "22" }}
            >
              <div
                className="mx-auto rounded-full flex items-center justify-center mb-2"
                style={{
                  width: 60,
                  height: 60,
                  background: g.color,
                  border: "3px solid #2b2b2b",
                  boxShadow: "0 3px 0 #2b2b2b",
                }}
              >
                <span className="text-3xl" aria-hidden="true">
                  {g.emoji}
                </span>
              </div>
              <div className="font-display text-sm sm:text-base leading-tight">
                {g.name}
              </div>
              <div
                className="text-xs mt-1"
                style={{ color: "#484848" }}
              >
                {g.tagline}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Fun fact / silly joke */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-8">
        <FunFact />
      </section>

      {/* Suggested arcade games */}
      <section
        className="mx-auto max-w-6xl px-4 sm:px-6 mt-8"
        data-testid="home-suggested-strip"
      >
        <div className="flex items-end justify-between mb-3 gap-3">
          <h2 className="font-display text-2xl sm:text-3xl flex items-center gap-2">
            <Trophy size={26} strokeWidth={3} style={{ color: "#FFB400" }} />
            Suggested for you
          </h2>
          <Link
            to="/games"
            className="btn-3d btn-sm"
            data-testid="home-see-all-games"
          >
            <Gamepad2 size={16} strokeWidth={3} />
            All games
          </Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4">
          {suggested.map((g) => (
            <Link
              key={g.id}
              to={`/games/${g.id}`}
              className="chunky-card pushable p-3 text-center"
              data-testid={`home-game-${g.id}`}
              style={{ background: g.color + "22" }}
            >
              <div
                className="mx-auto rounded-full flex items-center justify-center mb-2"
                style={{
                  width: 52,
                  height: 52,
                  background: g.color,
                  border: "3px solid #2b2b2b",
                  boxShadow: "0 3px 0 #2b2b2b",
                }}
              >
                <span className="text-2xl" aria-hidden="true">
                  {g.emoji}
                </span>
              </div>
              <div className="font-display text-xs sm:text-sm leading-tight">
                {g.name}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-10">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-2xl sm:text-3xl">
            Pick a wonder-land
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
          {CATEGORIES.map((cat) => (
            <Link
              to={`/category/${cat.id}`}
              key={cat.id}
              data-testid={`category-tile-${cat.id}`}
              className="chunky-card pushable overflow-hidden group"
            >
              <div
                className="relative flex items-center justify-center"
                style={{
                  background: cat.color,
                  height: 130,
                  borderBottom: "3px solid #2b2b2b",
                  overflow: "hidden",
                }}
              >
                <img
                  src={CATEGORY_HERO_IMAGES[cat.id]}
                  alt={`${cat.name} category — ${cat.tagline}`}
                  className="absolute inset-0 w-full h-full object-cover opacity-50"
                  loading="lazy"
                />
                <span
                  className="relative text-6xl bob"
                  style={{ filter: "drop-shadow(0 3px 0 rgba(0,0,0,0.25))" }}
                >
                  {cat.emoji}
                </span>
              </div>
              <div className="p-4">
                <div
                  className="font-display text-lg sm:text-xl leading-tight"
                  data-testid={`category-name-${cat.id}`}
                >
                  {cat.name}
                </div>
                <div
                  className="text-sm mt-1"
                  style={{ color: "#484848" }}
                >
                  {cat.tagline}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-12">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-2xl sm:text-3xl">
            <span className="wiggle inline-block mr-2">🌟</span>Today&apos;s picks
          </h2>
        </div>
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6"
          data-testid="featured-grid"
        >
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
